import { onLoad } from "@otto-ec/global-resources/dom";
import Combolist from "./domain/combolist";
import Combo from "./domain/combo";
import Article from "./domain/article";
import {
	sendArticleClickTrackingEvent,
	sendComboClickTrackingEvent,
	sendComboOpenTrackingEvent,
	sendLoadTrackingEvent,
	sendScrollRightOrLeftTrackingEvent,
	sendVisibleTrackingEvent
} from "./tracking_service";
import logger from "./logger";

const DEFAULT_WIDGET_POSITION = "1";
const OUTFIT_COMBOLIST_OBSERVER_THRESHOLD = 0.35;
const ROOM_COMBOLIST_OBSERVER_THRESHOLD = 0.5;
const SHEET_ALIGNMENT_BREAKPOINT = 1400;

export default class ComboCombolist {
	constructor(container) {
		this.container = container;
		this.id = container.querySelector(".combo_combolist-cinema").id;
		this.featureOrder = this.getFeatureOrder();
		this.featurePosition = this.getPosition();
		this.featureNumber = this.getFeatureNumber();
		this.promoType = container.dataset.promoType;
		this.sorting = container.dataset.sorting;
		this.bufferSlides = [];
		this.visibleSlidesBeforeScrolling = [];

		const combos = Array.prototype.map.call(this._getComboItems(), (comboHtml, index) => {
			const comboId = comboHtml.dataset.comboId;
			const comboSource = comboHtml.dataset.comboSource;
			const contentSource = comboHtml.dataset.comboModel;
			const articleElements = Array.from(comboHtml.querySelectorAll(".combo_combolist-article"));
			const articles = articleElements.map((articleHtml, articleIndex) => {
				return new Article(articleHtml.dataset.variationId, articleIndex + 1, articleHtml.dataset.availability);
			});
			return new Combo(comboId, false, comboSource, contentSource, articles, index + 1);
		});

		function _getMasFeatureTrackingLabels(combolistContainer) {
			return JSON.parse(
				combolistContainer.parentElement.parentElement.getAttribute("data-feature-tracking-labels")
			);
		}

		this.combolist = new Combolist(
			combos,
			this.featurePosition,
			this.featureNumber,
			this.promoType,
			this.sorting,
			_getMasFeatureTrackingLabels(container)
		);

		this.threshold =
			this.promoType === "ComboListDetailOutfit"
				? OUTFIT_COMBOLIST_OBSERVER_THRESHOLD
				: ROOM_COMBOLIST_OBSERVER_THRESHOLD;

		this.observer = new IntersectionObserver(
			(observerEntries, intersectionObserver) => {
				this.intersectionObserverCallback(observerEntries, intersectionObserver, this);
			},
			{
				threshold: this.threshold
			}
		);

		this._getComboItems().forEach(combo => {
			if (combo) {
				this.observer.observe(combo);
			}
		});

		this._setSheetAlignment();
	}

	_setSheetAlignment() {
		const sheet = this.container.querySelector("oc-sheet-v1");
		if (!sheet) {
			return;
		}
		const isWideViewport = window.innerWidth > SHEET_ALIGNMENT_BREAKPOINT;
		sheet.setAttribute("adaptive-alignment", isWideViewport ? "center" : "right");
	}

	initCinemaEvents() {
		const cinema = this.container.querySelector("oc-cinema-v1");
		/*                                                                                                                             */
		/*                                                             */
		const TIMEOUT_DELAY_300_MS = 300;
		setTimeout(() => {
			onLoad.subscribe(() => {
				if (!cinema.visibleItems) {
					logger.log("Cinema visibleItems is not defined. Cinema Load event will not be sent.");
					return;
				}
				const currentSlides = this._getCinemaIndicesForVisibleSlides(cinema);
				this.visibleSlidesBeforeScrolling = currentSlides;
				this._setVisibleSlides(currentSlides);
				sendLoadTrackingEvent(this.combolist, currentSlides);
			});
		}, TIMEOUT_DELAY_300_MS);

		cinema.addEventListener("oc-item-visibility-changed", event => {
			this._resetVisibleSlides();
			const visibleItemsIndices = event.detail.visibleItems.indices;
			this._setVisibleSlides(visibleItemsIndices);
			this.updateBufferSlides(visibleItemsIndices);
			if (event.detail.scrollDirection === "right" && !event.detail.stageIsScrolling) {
				sendScrollRightOrLeftTrackingEvent(this.combolist, this.featureOrder, this.bufferSlides, "next");
				this.clearBuffer();
				this.resetInitialVisibleBufferSlides(visibleItemsIndices);
			} else if (event.detail.scrollDirection === "left" && !event.detail.stageIsScrolling) {
				sendScrollRightOrLeftTrackingEvent(this.combolist, this.featureOrder, this.bufferSlides, "previous");
				this.clearBuffer();
				this.resetInitialVisibleBufferSlides(visibleItemsIndices);
			}
		});
	}

	clearBuffer() {
		this.bufferSlides = [];
	}

	resetInitialVisibleBufferSlides(visibleItems) {
		this.visibleSlidesBeforeScrolling = visibleItems;
	}

	updateBufferSlides(indices) {
		const notInInitialVisibleSlides = indices.filter(index => !this.visibleSlidesBeforeScrolling.includes(index));

		this.bufferSlides = Array.from(new Set(this.bufferSlides.concat(notInInitialVisibleSlides)));
	}

	_getCinemaIndicesForVisibleSlides(cinema) {
		return Array.from(cinema.visibleItems)
			.map(item => {
				const { comboId } = item.dataset;
				return this.combolist.combos.findIndex(combo => combo.id === comboId);
			})
			.filter(index => index !== -1);
	}

	_resetVisibleSlides() {
		Array.from(this.combolist.combos).forEach(combo => {
			combo.isVisible = false;
		});
	}

	_setVisibleSlides(currentSlides) {
		if (this.combolist?.combos) {
			for (const slideIndex of currentSlides) {
				const combo = this.combolist.combos[slideIndex];
				if (combo) {
					combo.isVisible = true;
				}
			}
		}
	}

	_setComboAsVisible(comboid) {
		this.combolist.combos.find(combo => combo.id === comboid).isVisible = true;
	}

	intersectionObserverCallback(entries, interSectionObserver, combolist) {
		if (entries.some(entry => entry.isIntersecting)) {
			this._resetVisibleSlides();
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					combolist._setComboAsVisible(entry.target.dataset.comboId);
				}
			});
			interSectionObserver.disconnect();
			sendVisibleTrackingEvent(this.combolist);
		}
	}

	oldAddClickEventListenersForComboMoodImage() {
		const links = this.container.querySelectorAll(".combo_combolist__mood-image-container a");
		Array.from(links).forEach(moodImage => {
			moodImage.addEventListener("click", event => {
				const comboId = this.getComboId(event.currentTarget);
				this.oldTrackComboClick(comboId);
			});
		});
	}

	addClickEventListenersForComboMoodImage() {
		const links = this.container.querySelectorAll(".combo_combolist__mood-image-container div");
		Array.from(links).forEach(moodImage => {
			moodImage.addEventListener("click", event => {
				const comboId = this.getComboId(event.currentTarget);
				this.trackComboClick(comboId);
			});
		});
	}

	addClickEventListenersForArticles() {
		const articles = this.container.querySelectorAll(".combo_combolist-article a");
		Array.from(articles).forEach(article => {
			article.addEventListener("click", event => {
				const variationId = event.currentTarget.closest(".combo_combolist-article").dataset.variationId;
				const comboId = this.getComboId(event.currentTarget);
				this.trackArticleClick(comboId, variationId);
			});
		});
	}

	oldTrackComboClick(comboId) {
		sendComboClickTrackingEvent(
			this.combolist,
			this.combolist.combos.find(combo => combo.id === comboId)
		);
	}

	trackComboClick(comboId) {
		sendComboOpenTrackingEvent(
			this.combolist,
			this.combolist.combos.find(combo => combo.id === comboId)
		);
	}

	trackArticleClick(comboId, variationId) {
		const clickedCombo = this.combolist.combos.find(combo => combo.id === comboId);
		const clickedArticle = clickedCombo.articles.find(article => article.variationId === variationId);
		sendArticleClickTrackingEvent(this.combolist, clickedCombo, clickedArticle);
	}

	isInViewport(elem) {
		const bounding = elem.getBoundingClientRect();
		return (
			bounding.top >= 0 &&
			bounding.left >= 0 &&
			bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
			bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	getComboId(clickableItem) {
		const listElement = clickableItem.closest(".combo_combolist-combo");
		return listElement.dataset.comboId;
	}

	getPosition() {
		const featureOrder = this.container.parentElement.parentElement.dataset.featureOrder;
		return Number.parseInt(featureOrder || DEFAULT_WIDGET_POSITION, 10);
	}

	getFeatureNumber() {
		const featurePosition = this.getPosition();
		const combolistWidgets = document.querySelectorAll(".combo_combolist-container");
		return (
			Array.from(combolistWidgets).findIndex(widgetDiv => {
				const featureOrder = widgetDiv.parentElement.parentElement.dataset.featureOrder;
				return Number.parseInt(featureOrder || DEFAULT_WIDGET_POSITION, 10) === featurePosition;
			}) + 1
		);
	}

	getFeatureOrder() {
		const featurePosition = this.getPosition();
		const featureNumber = this.getFeatureNumber();
		return `${featurePosition}$${featureNumber}`;
	}

	getAllComboIds() {
		const comboItems = this._getComboItems();

		return Array.from(comboItems).map(div => div.dataset.comboId);
	}

	_getComboItems() {
		return this.container.querySelectorAll(".combo_combolist-combo");
	}
}
